/**
 * IndexedDB สำหรับเก็บคิวรายการที่ยังส่งไม่สำเร็จ
 *
 * เก็บ Blob ของรูปไว้ด้วย เพราะถ้าเก็บแค่ path ไฟล์ พอผู้ใช้ปิดแอปแล้วเปิดใหม่
 * จะเข้าถึงไฟล์เดิมไม่ได้ ต้องให้ผู้ใช้ถ่ายใหม่ทั้งที่ข้อมูลอย่างอื่นครบแล้ว
 *
 * ข้อจำกัดที่ต้องบอกผู้ใช้: การล้างข้อมูลเบราว์เซอร์จะลบคิวนี้ทิ้ง
 * และ iOS อาจเก็บกวาดข้อมูลของเว็บที่ไม่ได้เปิดนานหลายสัปดาห์
 * จึงต้องเตือนผู้ใช้ให้ส่งคิวให้หมดก่อนเลิกงาน
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type QueueStatus = 'pending' | 'sending' | 'sent' | 'failed';

export const QUEUE_STATUS_LABELS_TH: Record<QueueStatus, string> = {
  pending: 'รอส่ง',
  sending: 'กำลังส่ง',
  sent: 'ส่งสำเร็จ',
  failed: 'ส่งไม่สำเร็จ',
};

export interface QueuedCapture {
  /** ใช้ idempotency key เป็น primary key — ยิงซ้ำกี่ครั้งก็ได้รายการเดียว */
  id: string;
  status: QueueStatus;

  /** ข้อมูลที่จะส่งไป POST /api/captures/draft (ยกเว้นตัวไฟล์รูป) */
  customerRegistration: string;
  gps: {
    latitude: number;
    longitude: number;
    accuracyM: number;
    altitude: number | null;
    heading: number | null;
    speed: number | null;
    positionTimestamp: number;
  };
  note?: string;
  gpsOverrideReason?: string;
  allowDuplicate?: boolean;
  duplicateReason?: string;

  /** ไฟล์รูปที่ประมวลผลแล้ว */
  imageBlob: Blob;
  imageContentType: 'image/jpeg' | 'image/webp' | 'image/png';
  imageSizeBytes: number;
  imageSha256: string;

  /** ความคืบหน้า — ใช้ทำงานต่อจากจุดเดิมหลังเน็ตหลุด */
  captureId: string | null;
  uploadToken: string | null;
  uploadTokenExpiresAt: string | null;
  uploaded: boolean;
  awaitingGpsApproval: boolean;

  attempts: number;
  /** epoch ms — ไม่ลองใหม่ก่อนถึงเวลานี้ (exponential backoff) */
  nextAttemptAt: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;

  createdAt: string;
  updatedAt: string;
}

interface MeterPointDB extends DBSchema {
  queue: {
    key: string;
    value: QueuedCapture;
    indexes: { 'by-status': QueueStatus; 'by-created': string };
  };
  /** ค่าเล็ก ๆ ที่ต้องอยู่ข้ามการเปิดปิดแอป เช่น โหมดธีมที่เลือกไว้ */
  prefs: {
    key: string;
    value: unknown;
  };
}

const DB_NAME = 'meterpoint';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<MeterPointDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<MeterPointDB>> {
  dbPromise ??= openDB<MeterPointDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const queue = db.createObjectStore('queue', { keyPath: 'id' });
      queue.createIndex('by-status', 'status');
      queue.createIndex('by-created', 'createdAt');
      db.createObjectStore('prefs');
    },
  });
  return dbPromise;
}

export async function putQueued(item: QueuedCapture): Promise<void> {
  const db = await getDb();
  await db.put('queue', { ...item, updatedAt: new Date().toISOString() });
}

export async function getQueued(id: string): Promise<QueuedCapture | undefined> {
  const db = await getDb();
  return db.get('queue', id);
}

export async function allQueued(): Promise<QueuedCapture[]> {
  const db = await getDb();
  const items = await db.getAll('queue');
  return items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** รายการที่พร้อมส่ง ณ เวลานี้ (ยังไม่ถึงเวลา retry จะยังไม่ถูกหยิบ) */
export async function claimableQueued(now = Date.now()): Promise<QueuedCapture[]> {
  const items = await allQueued();
  return items
    .filter((item) => item.status === 'pending' || item.status === 'failed')
    .filter((item) => item.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function deleteQueued(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('queue', id);
}

/** ลบรายการที่ส่งสำเร็จและเก่ากว่าที่กำหนด เพื่อไม่ให้ IndexedDB โตไม่มีที่สิ้นสุด */
export async function purgeSent(olderThanMs = 24 * 60 * 60 * 1000): Promise<number> {
  const db = await getDb();
  const cutoff = new Date(Date.now() - olderThanMs).toISOString();
  const items = await db.getAll('queue');
  let removed = 0;
  for (const item of items) {
    if (item.status === 'sent' && item.updatedAt < cutoff) {
      await db.delete('queue', item.id);
      removed += 1;
    }
  }
  return removed;
}

export async function countByStatus(): Promise<Record<QueueStatus, number>> {
  const items = await allQueued();
  const counts: Record<QueueStatus, number> = { pending: 0, sending: 0, sent: 0, failed: 0 };
  for (const item of items) counts[item.status] += 1;
  return counts;
}

export async function getPref<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  return (await db.get('prefs', key)) as T | undefined;
}

export async function setPref(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  await db.put('prefs', value, key);
}

/**
 * ประเมินพื้นที่จัดเก็บที่เหลือ ใช้เตือนผู้ใช้ก่อนที่คิวจะเขียนไม่ลง
 * ไม่ใช่ทุกเบราว์เซอร์รองรับ จึงคืน null ได้
 */
export async function storageEstimate(): Promise<{ usedMb: number; quotaMb: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage, quota } = await navigator.storage.estimate();
  if (usage === undefined || quota === undefined) return null;
  return { usedMb: usage / 1024 / 1024, quotaMb: quota / 1024 / 1024 };
}

/**
 * ขอให้เบราว์เซอร์ไม่ลบข้อมูลของเราตอนพื้นที่ไม่พอ
 * ต้องเรียกหลังจากผู้ใช้เริ่มใช้งานจริงแล้ว เบราว์เซอร์ถึงจะอนุมัติ
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted?.()) return true;
  return navigator.storage.persist();
}
